import React, { useState, useEffect } from 'react';
import { Label } from './label';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

interface ReviewPeriodOption {
  value: string;
  label: string;
  days: number;
}

interface ReviewPeriodSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showPreview?: boolean;
  currentReviewDate?: string;
}

/**
 * ReviewPeriodSelect component for selecting review intervals
 * Supports periods from 1 day to several years with smart defaults
 */
export const ReviewPeriodSelect: React.FC<ReviewPeriodSelectProps> = ({
  value = '1y',
  onChange,
  label = 'Review Period',
  required = false,
  disabled = false,
  className = '',
  showPreview = true,
  currentReviewDate
}) => {
  const [options, setOptions] = useState<ReviewPeriodOption[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Load review period options from API
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/assets/review-period-options') as any;
        if (response.success) {
          setOptions(response.data);
        }
      } catch (error) {
        console.error('Failed to load review period options:', error);
        // Fallback options if API fails
        setOptions([
          { value: '1d', label: '1 den', days: 1 },
          { value: '7d', label: '1 týden', days: 7 },
          { value: '30d', label: '1 měsíc', days: 30 },
          { value: '3m', label: '3 měsíce', days: 90 },
          { value: '6m', label: '6 měsíců', days: 180 },
          { value: '1y', label: '1 rok', days: 365 },
          { value: '2y', label: '2 roky', days: 730 },
          { value: '3y', label: '3 roky', days: 1095 }
        ]);
        toast({
          title: 'Upozornění',
          description: 'Načítání možností review periody selhalo, používám výchozí hodnoty',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, [toast]);

  // Calculate preview date
  const getPreviewDate = () => {
    if (!showPreview || !value) return null;
    
    const selectedOption = options.find(opt => opt.value === value);
    if (!selectedOption) return null;

    const fromDate = currentReviewDate ? new Date(currentReviewDate) : new Date();
    const previewDate = new Date(fromDate);
    previewDate.setDate(previewDate.getDate() + selectedOption.days);
    
    return previewDate.toLocaleDateString('cs-CZ');
  };

  const previewDate = getPreviewDate();

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="w-full h-10 bg-muted animate-pulse rounded-md"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="review_period">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      
      <select
        id="review_period"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {previewDate && showPreview && (
        <div className="text-xs text-muted-foreground">
          Další review: {previewDate}
        </div>
      )}
    </div>
  );
};
